import Phaser from 'phaser';
import {
  type BagSlot,
  DUPLICATE_REFUND,
  GOLDEN_BAG_ODDS,
  GOLDEN_DUPLICATE_REFUND,
  RARITY_COLORS,
  TAKEOUT_BAG_ODDS,
  rollTable,
} from '../config/restaurant';
import { getAudioService } from './audio/AudioService';
import { CAPSULE_DUPLICATE_REFUND, CAPSULE_ODDS } from '../config/capybaraEvent';
import { TOWER_NAMES } from '../config/displayNames';
import type { TowerType } from '../core/types';
import type { ProgressService } from '../services/progressService';
import { RankService } from '../services/rankService';
import { StatsService } from '../services/statsService';
import { saveStorage } from './saveStorage';
import { recordStat } from './quests';
import { enqueueAchievementToast, sweepAndToastAchievements } from '../ui/achievementToast';

/**
 * Opening a prize box: the roulette, then the reveal.
 *
 * Shared by the Restaurant (open the bag you just cooked), Storage (open one
 * off the shelf) and the Capybara Event's capsule, because all of them must
 * show the identical ceremony — the strip of cards sliding under a centre
 * marker and slowing to a stop on the tower you actually got, then that tower
 * blown up large with its name underneath.
 *
 * The winner is decided BEFORE the animation starts and the strip is built to
 * land on it, so the picture can never disagree with what was granted. The
 * grant itself happens here too (via ProgressService), the instant the roll is
 * made rather than when the animation ends — closing the tab mid-spin must not
 * cost the player their tower.
 *
 * What differs between the boxes is gathered into `PrizeBoxKind` below; the
 * ceremony itself knows nothing about which one it is showing.
 */

/** One openable box: its table, its wording, and what a duplicate is worth. */
export interface PrizeBoxKind {
  /** The drop table — also the filler roster and the rarity source. */
  readonly odds: readonly BagSlot[];
  /** The line over the spinning strip. */
  readonly title: string;
  /** What a tower the player already owns pays back instead. */
  readonly duplicateRefund: number;
  /** Pays that refund, in whichever currency this box deals in. */
  readonly payRefund: (progress: ProgressService, amount: number) => void;
  /** "Sold for 75 Hambi Coins" — the reveal's duplicate line. */
  readonly refundLabel: (amount: number) => string;
  /**
   * Whether this open counts as a TAKEOUT BAG for the achievements and the
   * lifetime counters (Gambling, Doubles, `bagsOpened`). The capsule is its
   * own thing and deliberately feeds none of them; a NEW tower still records
   * an `obtains` either way, because it is one.
   */
  readonly bagHooks: boolean;
}

export const TAKEOUT_BAG_BOX: PrizeBoxKind = {
  odds: TAKEOUT_BAG_ODDS,
  title: 'OPENING TAKEOUT BAG…',
  duplicateRefund: DUPLICATE_REFUND,
  payRefund: (progress, amount) => progress.addCoins(amount),
  refundLabel: (amount) => `Sold for ${amount} Hambi Coins`,
  bagHooks: true,
};

/**
 * The Golden Takeout Bag (spec 2026-09-26). It IS a takeout bag, so it feeds
 * Gambling, Doubles and `bagsOpened` exactly as the Normal one does.
 */
export const GOLDEN_BAG_BOX: PrizeBoxKind = {
  odds: GOLDEN_BAG_ODDS,
  title: 'OPENING GOLDEN TAKEOUT BAG…',
  duplicateRefund: GOLDEN_DUPLICATE_REFUND,
  payRefund: (progress, amount) => progress.addCoins(amount),
  refundLabel: (amount) => `Sold for ${amount} Hambi Coins`,
  bagHooks: true,
};

export const CAPSULE_BOX: PrizeBoxKind = {
  odds: CAPSULE_ODDS,
  title: 'OPENING CAPSULE…',
  duplicateRefund: CAPSULE_DUPLICATE_REFUND,
  payRefund: (progress, amount) => progress.addCapycoins(amount),
  refundLabel: (amount) => `Sold for ${amount} Capycoins`,
  bagHooks: false,
};

/** The slot a tower occupies in this box, or undefined if it can't drop it. */
function slotFor(kind: PrizeBoxKind, tower: TowerType): BagSlot | undefined {
  return kind.odds.find((s) => s.tower === tower);
}

/** Roulette card geometry. */
const CARD_W = 132;
const CARD_H = 168;
const CARD_GAP = 12;
const CARD_PITCH = CARD_W + CARD_GAP;
/** Cards built into the strip. The winner sits near the end (see WINNER_INDEX). */
const STRIP_LEN = 44;
const WINNER_INDEX = 38;
/**
 * The spinning sound, played once as the strip sets off. One key for every
 * box — the Normal and Golden bags and the capsule all spin the same strip,
 * so they share the sound too. Its length matches `SPIN_MS`.
 */
export const SPIN_SFX_KEY = 'sfx-roulette-spin';
/** Spin duration (ms) and the y the strip rides at. */
const SPIN_MS = 4200;
const STRIP_Y = 330;

const OVERLAY_DEPTH = 400;

export interface BagOpenResult {
  tower: TowerType;
  /** True when the player already owned it — the coins were refunded instead. */
  duplicate: boolean;
}

export interface BagOpenOptions {
  progress: ProgressService;
  /** Injected for the tests/e2e; defaults to Math.random. */
  rand?: () => number;
  /** Fired after the player dismisses the reveal. */
  onDone: (result: BagOpenResult) => void;
}

/** Tier-1 art for a tower, falling back to the Hambi Tower if it isn't loaded. */
function artKey(scene: Phaser.Scene, type: TowerType): string {
  return scene.textures.exists(`tower-${type}-1`) ? `tower-${type}-1` : 'tower-patty-1';
}

/** Roll a Normal Takeout Bag and play its ceremony. */
export function openTakeoutBag(scene: Phaser.Scene, opts: BagOpenOptions): BagOpenResult {
  return openPrizeBox(scene, TAKEOUT_BAG_BOX, opts);
}

/** Roll a Golden Takeout Bag and play its ceremony. */
export function openGoldenBag(scene: Phaser.Scene, opts: BagOpenOptions): BagOpenResult {
  return openPrizeBox(scene, GOLDEN_BAG_BOX, opts);
}

/** Roll a Capybara Event capsule and play its ceremony. */
export function openCapsule(scene: Phaser.Scene, opts: BagOpenOptions): BagOpenResult {
  return openPrizeBox(scene, CAPSULE_BOX, opts);
}

/**
 * Roll a box, grant (or refund) it, and play the full open ceremony over
 * whatever scene is showing. Returns the result immediately as well as passing
 * it to `onDone`, so a caller can update its own header without waiting.
 */
export function openPrizeBox(
  scene: Phaser.Scene,
  kind: PrizeBoxKind,
  opts: BagOpenOptions,
): BagOpenResult {
  const rand = opts.rand ?? Math.random;
  const tower = rollTable(kind.odds, rand);

  // Grant first, animate second. `awardTower` reports the duplicate, and is the
  // single place ownership is decided — so a bag pays out exactly once even if
  // the player closes the tab halfway through the spin.
  const newlyOwned = opts.progress.awardTower(
    tower as Parameters<ProgressService['awardTower']>[0],
  );
  const duplicate = !newlyOwned;
  // Quests: a bag is opened either way; only a NEW tower is an obtain.
  // Recorded before the refund so a fresh period baselines first.
  if (kind.bagHooks) recordStat('bagsOpened');
  if (newlyOwned) recordStat('obtains');
  if (duplicate) kind.payRefund(opts.progress, kind.duplicateRefund);

  const result: BagOpenResult = { tower, duplicate };

  // Achievements: first bag ever, Doubles (same roll twice in a row), then the
  // state sweep (a Fire Mage / Aqua Sorcerer roll may have just satisfied one).
  // The capsule is not a bag and skips all of it.
  if (kind.bagHooks)
    try {
      const rankSvc = new RankService(saveStorage());
      const stats = new StatsService(saveStorage());
      const g = rankSvc.award('gambling');
      if (g) enqueueAchievementToast({ title: g.name, body: g.how, iconKey: g.icon });
      if (stats.lastBag() === tower) {
        const d = rankSvc.award('doubles');
        if (d) enqueueAchievementToast({ title: d.name, body: d.how, iconKey: d.icon });
      }
      stats.setLastBag(tower);
      sweepAndToastAchievements(saveStorage());
    } catch (err) {
      console.warn('[bagOpening] achievement hooks failed', err);
    }

  playRoulette(scene, kind, tower, rand, () =>
    showReveal(scene, kind, result, () => opts.onDone(result)),
  );
  return result;
}

/**
 * The horizontal card strip. It is built with the winner at a fixed index and
 * then tweened so exactly that card ends under the centre marker — the arrow
 * lands on what you got because the maths says so, not because it was timed.
 */
function playRoulette(
  scene: Phaser.Scene,
  kind: PrizeBoxKind,
  winner: TowerType,
  rand: () => number,
  onDone: () => void,
): void {
  const W = 1280;
  const H = 720;
  const objs: Phaser.GameObjects.GameObject[] = [];

  // Nearly opaque: the host screen's own header sits right where the roulette
  // title goes, and a lighter dim left the two reading on top of each other.
  objs.push(scene.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.95).setDepth(OVERLAY_DEPTH));
  objs.push(
    scene.add
      .text(W / 2, 150, kind.title, {
        fontSize: '30px',
        color: '#f5c518',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 1),
  );

  // Masked lane so cards appear to fly in from off-stage rather than popping in.
  const laneTop = STRIP_Y - CARD_H / 2 - 10;
  const laneH = CARD_H + 20;
  objs.push(
    scene.add
      .rectangle(W / 2, STRIP_Y, W - 120, laneH, 0x0d0b16, 0.95)
      .setStrokeStyle(2, 0x5a4a2a)
      .setDepth(OVERLAY_DEPTH + 1),
  );

  const strip = scene.add.container(0, 0).setDepth(OVERLAY_DEPTH + 2);
  objs.push(strip);
  for (let i = 0; i < STRIP_LEN; i++) {
    // Every card except the winner's slot is filler — drawn from the bag's own
    // roster so the strip looks like the table it came from.
    const type =
      i === WINNER_INDEX ? winner : kind.odds[Math.floor(rand() * kind.odds.length)].tower;
    strip.add(buildCard(scene, kind, i * CARD_PITCH, STRIP_Y, type));
  }

  const maskShape = scene.make.graphics({ x: 0, y: 0 });
  maskShape.fillStyle(0xffffff);
  maskShape.fillRect(60, laneTop, W - 120, laneH);
  strip.setMask(maskShape.createGeometryMask());

  // Centre marker — a green glow column with an arrow above and below, exactly
  // like the reference. Drawn ABOVE the strip so the winning card sits under it.
  const marker = scene.add.graphics().setDepth(OVERLAY_DEPTH + 3);
  marker.fillStyle(0x6ecb3c, 0.22);
  marker.fillRect(W / 2 - 4, laneTop, 8, laneH);
  marker.fillStyle(0x8be04a, 1);
  marker.fillTriangle(W / 2 - 14, laneTop - 20, W / 2 + 14, laneTop - 20, W / 2, laneTop - 2);
  marker.fillTriangle(
    W / 2 - 14,
    laneTop + laneH + 20,
    W / 2 + 14,
    laneTop + laneH + 20,
    W / 2,
    laneTop + laneH + 2,
  );
  objs.push(marker);

  // Land the winner's centre on x = W/2. Cards are positioned by their centre,
  // so the container offset is simply "centre minus the winner's local x".
  const endX = W / 2 - WINNER_INDEX * CARD_PITCH;
  strip.x = W / 2 - 2 * CARD_PITCH; // start with the first few cards on screen
  getAudioService(scene).play(SPIN_SFX_KEY);
  scene.tweens.add({
    targets: strip,
    x: endX,
    duration: SPIN_MS,
    ease: 'Cubic.easeOut',
    onComplete: () => {
      // A short beat on the landed card before the reveal takes over.
      scene.time.delayedCall(600, () => {
        strip.clearMask(true);
        objs.forEach((o) => o.destroy());
        onDone();
      });
    },
  });
}

/** One roulette card: rarity-coloured frame, tower art, name, rarity ribbon. */
function buildCard(
  scene: Phaser.Scene,
  kind: PrizeBoxKind,
  x: number,
  y: number,
  type: TowerType,
): Phaser.GameObjects.Container {
  const slot = slotFor(kind, type);
  const color = slot ? RARITY_COLORS[slot.rarity] : 0x8b949c;
  const card = scene.add.container(x, y);

  const bg = scene.add.rectangle(0, 0, CARD_W, CARD_H, 0x1b1830, 1).setStrokeStyle(3, color);
  card.add(bg);
  card.add(
    scene.add
      .text(0, -CARD_H / 2 + 16, TOWER_NAMES[type], {
        fontSize: '13px',
        color: '#ffffff',
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: CARD_W - 14 },
      })
      .setOrigin(0.5),
  );
  card.add(scene.add.image(0, 6, artKey(scene, type)).setDisplaySize(84, 84));
  card.add(scene.add.rectangle(0, CARD_H / 2 - 18, CARD_W - 16, 22, color, 1));
  card.add(
    scene.add
      .text(0, CARD_H / 2 - 18, slot?.rarity ?? 'Common', {
        fontSize: '12px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5),
  );
  return card;
}

/**
 * The reveal: the tower enlarged centre-screen with its name beneath, plus the
 * duplicate refund line when the player already had it, and the OK button.
 *
 * The OK button used to be withheld for five seconds "so the prize is actually
 * looked at". It is now built with everything else: the 4.2s roulette spin has
 * already made the player wait, and a second forced hold only made the reveal
 * feel unresponsive. The 420ms art pop-in below still plays — that is the
 * reveal itself, not a wait — and nothing stops a player reading the card for
 * as long as they like before pressing OK.
 */
function showReveal(
  scene: Phaser.Scene,
  kind: PrizeBoxKind,
  result: BagOpenResult,
  onClose: () => void,
): void {
  const W = 1280;
  const H = 720;
  const objs: Phaser.GameObjects.GameObject[] = [];
  const slot = slotFor(kind, result.tower);
  const color = slot ? RARITY_COLORS[slot.rarity] : 0x8b949c;

  objs.push(scene.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.88).setDepth(OVERLAY_DEPTH));
  objs.push(
    scene.add
      .rectangle(W / 2, H / 2 - 20, 560, 460, 0x141126, 1)
      .setStrokeStyle(4, color)
      .setDepth(OVERLAY_DEPTH + 1),
  );
  objs.push(
    scene.add
      .text(W / 2, H / 2 - 214, slot ? slot.rarity.toUpperCase() : 'YOU GOT', {
        fontSize: '22px',
        color: Phaser.Display.Color.IntegerToColor(color).rgba,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 2),
  );

  // The prize, enlarged — it pops from small so the landing reads as an arrival.
  const art = scene.add
    .image(W / 2, H / 2 - 70, artKey(scene, result.tower))
    .setDisplaySize(40, 40)
    .setDepth(OVERLAY_DEPTH + 2);
  objs.push(art);
  scene.tweens.add({
    targets: art,
    displayWidth: 230,
    displayHeight: 230,
    duration: 420,
    ease: 'Back.easeOut',
  });

  objs.push(
    scene.add
      .text(W / 2, H / 2 + 90, TOWER_NAMES[result.tower], {
        fontSize: '34px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 2),
  );
  objs.push(
    scene.add
      .text(
        W / 2,
        H / 2 + 132,
        result.duplicate
          ? kind.refundLabel(kind.duplicateRefund)
          : 'Unlocked! Field him via EQUIP LOADOUT in the Shop.',
        {
          fontSize: result.duplicate ? '22px' : '17px',
          color: result.duplicate ? '#f5c518' : '#9fd8ff',
          fontStyle: result.duplicate ? 'bold' : 'normal',
        },
      )
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 2),
  );

  const btnY = H / 2 + 186;
  const btn = scene.add
    .rectangle(W / 2, btnY, 200, 56, 0x1a6b1a)
    .setStrokeStyle(2, 0x4caf50)
    .setInteractive({ useHandCursor: true })
    .setDepth(OVERLAY_DEPTH + 2)
    .setName('bag-reveal-ok');
  objs.push(btn);
  objs.push(
    scene.add
      .text(W / 2, btnY, 'OK', { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 3),
  );
  btn.on('pointerup', () => {
    objs.forEach((o) => o.destroy());
    onClose();
  });
}
